import { useSearchParams } from 'react-router-dom';
import ErrorBoundary from '../components/ErrorBoundary';
import LearningGrid from './LearningGrid';
import LearningMacro from './LearningMacro';

// Learning Loop orchestrator (spec 9.6). Two-level view, built as a safe swap:
//   * macro band  — the PRESERVED long-horizon (3-5y) spine-learning screen (LearningMacro),
//                   always rendered, on its untouched /api/learning data path.
//   * fast grid   — the ADDITIVE per-intervention grid (LearningGrid), independently failable:
//                   its own loading/empty/error states plus an error boundary, so if it fails
//                   the macro band above still renders. The screen can't regress.
//
// Flag: `?view=v1` shows the macro band alone (the pre-existing screen); the default `v2` shows
// both. The macro band stays live behind v1 until v2 is verified; v2 is now the default.
export default function LearningLoop() {
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'v1' ? 'v1' : 'v2';

  const setView = (next: 'v1' | 'v2') => {
    const updated = new URLSearchParams(params);
    if (next === 'v1') updated.set('view', 'v1');
    else updated.delete('view');
    setParams(updated, { replace: true });
  };

  return (
    <div>
      <div className="row" style={{ justifyContent: 'flex-end', gap: 6, marginBottom: 8 }}>
        <span className="small faint" style={{ alignSelf: 'center' }}>View</span>
        <button
          type="button"
          className={`tag ${view === 'v2' ? 'teal' : 'muted'}`}
          style={{ cursor: 'pointer', border: 'none' }}
          aria-pressed={view === 'v2'}
          onClick={() => setView('v2')}
        >
          Two-level
        </button>
        <button
          type="button"
          className={`tag ${view === 'v1' ? 'teal' : 'muted'}`}
          style={{ cursor: 'pointer', border: 'none' }}
          aria-pressed={view === 'v1'}
          onClick={() => setView('v1')}
        >
          Macro only
        </button>
      </div>

      <LearningMacro />

      {view === 'v2' && (
        <section style={{ marginTop: 18 }}>
          <div className="ph">Fast loop · per-intervention grid</div>
          <p className="lead" style={{ marginBottom: 12 }}>
            The fast loop: high-frequency micro-decisions whose predictions close against outcomes in
            days, not years. Each row earns its accuracy curve as observations accumulate.
          </p>
          <ErrorBoundary
            fallback={
              <div className="small faint">
                Per-intervention grid failed to render (the macro band above is unaffected).
              </div>
            }
          >
            <LearningGrid />
          </ErrorBoundary>
        </section>
      )}
    </div>
  );
}
